import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import "./index.css";

// One-time kill-switch: unregister the legacy service worker that broke iPhone
// Safari and wipe its caches. Runs once per browser, then no-ops so future PWA
// installs work normally.
if (typeof window !== "undefined" && "serviceWorker" in navigator) {
  const SW_CLEANUP_KEY = "milon.swCleanupDone.v1";
  if (!localStorage.getItem(SW_CLEANUP_KEY)) {
    navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((r) => r.unregister());
    }).catch(() => {});
    if ("caches" in window) {
      caches.keys().then((keys) => {
        keys.forEach((k) => caches.delete(k));
      }).catch(() => {});
    }
    try { localStorage.setItem(SW_CLEANUP_KEY, "1"); } catch { /* ignore */ }
  }
}

// Auto-recover from stale lazy-chunk errors. After a deploy/HMR, the browser
// may still hold a route's old chunk URL; the new server returns 404 and
// React Router throws "Failed to fetch dynamically imported module". One
// hard reload picks up the new manifest. We guard with sessionStorage so a
// genuinely broken chunk doesn't loop forever.
if (typeof window !== "undefined") {
  const onChunkError = (msg: string) => {
    if (!/dynamically imported module|Importing a module script failed/i.test(msg)) return;
    const KEY = "__chunk_reload__";
    if (sessionStorage.getItem(KEY)) return;
    sessionStorage.setItem(KEY, "1");
    setTimeout(() => sessionStorage.removeItem(KEY), 10_000);
    window.location.reload();
  };
  window.addEventListener("error", (e) => onChunkError(String(e?.message || "")));
  window.addEventListener("unhandledrejection", (e) => onChunkError(String((e as PromiseRejectionEvent)?.reason?.message || "")));
}

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Milon could not find its application root.");
}

const root = createRoot(rootElement);
const cloudUrl = import.meta.env.VITE_SUPABASE_URL?.trim();
const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY?.trim();

const renderUnavailable = () => {
  root.render(
    <main className="min-h-screen bg-background px-6 grid place-items-center text-center">
      <section className="max-w-md" aria-labelledby="service-unavailable-title">
        <p className="font-serif text-3xl text-primary mb-3">Milon</p>
        <h1 id="service-unavailable-title" className="text-xl font-semibold text-foreground">
          Temporarily unavailable
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          We couldn&apos;t connect securely. Please try again in a few minutes.
        </p>
        <a
          href={window.location.href}
          className="mt-6 inline-flex h-11 items-center justify-center rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground"
        >
          Try again
        </a>
      </section>
    </main>,
  );
};

if (!cloudUrl || !publishableKey) {
  console.error("Milon deployment configuration is unavailable.");
  renderUnavailable();
} else {
  void import("./App.tsx")
    .then(({ default: App }) => {
      root.render(
        <HelmetProvider>
          <App />
        </HelmetProvider>,
      );
    })
    .catch((error: unknown) => {
      console.error("Milon failed to start.", error);
      renderUnavailable();
    });
}
